import React, { useMemo } from 'react';
import { AlertTriangle, CheckCircle2, Download, FileArchive } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../../ui/dialog';
import type { LayoutExportBundle } from '../../../../services/geoExport/layoutExportBundle';
import type { StudioExportFormat } from '../../../../services/geoExport/exportBundleDownloads';

export interface StudioExportDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  bundle: LayoutExportBundle | null;
  format: StudioExportFormat | null;
  exporting: boolean;
  onConfirm: () => void;
}

const FORMAT_LABELS: Record<StudioExportFormat, string> = {
  kmz: 'KMZ',
  shapefile: 'ESRI shapefile',
  dxf: 'DXF',
  dwg: 'DWG',
};

const FORMAT_LIMITATIONS: Record<StudioExportFormat, string> = {
  kmz: 'KMZ preserves WGS84 geometry and allowlisted metadata in ExtendedData. CAD-native blocks, hatches, and dimensions are geometry-only.',
  shapefile: 'The ZIP separates point, line, and polygon geometry. DBF field names and text values are truncated for legacy shapefile compatibility; manifest.json retains the full bundle.',
  dxf: 'DXF writes exact geometry in a local metre frame. Stable IDs and screening metadata are comments, which some CAD tools may discard.',
  dwg: 'DWG conversion starts from the same local-metre DXF. Converted geometry remains exact, but comments and extended metadata may not survive the converter.',
};

export const StudioExportDialog: React.FC<StudioExportDialogProps> = ({
  open,
  onOpenChange,
  bundle,
  format,
  exporting,
  onConfirm,
}) => {
  const counts = useMemo(() => {
    const features = bundle?.features ?? [];
    const layers = new Map<string, number>();
    for (const feature of features) {
      layers.set(feature.category, (layers.get(feature.category) ?? 0) + 1);
    }
    return {
      polygons: features.filter(feature => feature.geometry.type === 'polygon').length,
      lines: features.filter(feature => feature.geometry.type === 'line').length,
      points: features.filter(feature => feature.geometry.type === 'point').length,
      references: features.filter(feature => feature.category.startsWith('reference:')).length,
      layers: [...layers.entries()].sort(([left], [right]) => left.localeCompare(right)),
    };
  }, [bundle]);

  const verification = bundle?.manifest.verification;
  const unresolvedIssues = verification?.issues ?? [];
  const hasBlockingIssue = verification == null
    || unresolvedIssues.length > 0
    || verification.status !== 'fits'
    || verification.parcelEdgeSetbackSatisfied !== true;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="md"
        data-testid="studio-export-preflight"
        overlayClassName="z-[70]"
        className="z-[71] border border-border bg-card rounded-md"
      >
        <DialogHeader>
          <div className="flex items-center gap-2 text-primary">
            <FileArchive className="h-4 w-4" aria-hidden />
            <DialogTitle>Review export</DialogTitle>
          </div>
          <DialogDescription>
            Confirm the coordinate frame, included geometry, and current fit status before downloading.
          </DialogDescription>
        </DialogHeader>

        {bundle && format && (
          <div className="space-y-3 text-xs">
            <dl className="grid grid-cols-[8.5rem_minmax(0,1fr)] gap-x-3 gap-y-1 border border-border bg-background/60 p-3 rounded-lg">
              <dt className="text-text-muted">Format</dt>
              <dd className="font-semibold text-text-primary">{FORMAT_LABELS[format]}</dd>
              <dt className="text-text-muted">File stem</dt>
              <dd className="truncate font-mono text-text-primary">{bundle.name}</dd>
              <dt className="text-text-muted">Scenario</dt>
              <dd className="font-mono text-text-primary">{bundle.manifest.scenarioId ?? 'draft'}</dd>
              <dt className="text-text-muted">Revision</dt>
              <dd className="break-words font-mono text-text-primary">{bundle.manifest.revision ?? 'Unversioned'}</dd>
              <dt className="text-text-muted">CRS</dt>
              <dd className="font-mono text-text-primary">{bundle.manifest.crs}</dd>
              <dt className="text-text-muted">Local origin</dt>
              <dd className="font-mono text-text-primary">
                {bundle.manifest.origin
                  ? `${bundle.manifest.origin.lat.toFixed(8)}, ${bundle.manifest.origin.lng.toFixed(8)}`
                  : 'Derived from geometry'}
              </dd>
              <dt className="text-text-muted">Local CAD units</dt>
              <dd className="text-text-primary">metres</dd>
              <dt className="text-text-muted">Bundle schema</dt>
              <dd className="font-mono text-text-primary">v{bundle.schemaVersion}</dd>
              <dt className="text-text-muted">Geometry</dt>
              <dd className="text-text-primary">
                {counts.polygons} polygons · {counts.lines} lines · {counts.points} points
              </dd>
              <dt className="text-text-muted">References</dt>
              <dd className="text-text-primary">{counts.references} visible layers</dd>
            </dl>

            <div className="border border-border bg-background/40 p-3 rounded-lg">
              <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-text-muted">
                Included layers
              </p>
              <ul className="grid gap-1 sm:grid-cols-2" aria-label="Included export layers">
                {counts.layers.map(([category, count]) => (
                  <li key={category} className="flex justify-between gap-3 text-text-secondary">
                    <span className="truncate">{category}</span>
                    <span className="font-mono text-text-primary">{count}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div
              className={`flex items-start gap-2 border p-3 ${
                hasBlockingIssue
                  ? 'border-warning/50 bg-warning/5 text-warning'
                  : 'border-success/50 bg-success/5 text-success'
              } rounded-lg`}
              role="status"
            >
              {hasBlockingIssue
                ? <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                : <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />}
              <p>
                {hasBlockingIssue
                  ? `${unresolvedIssues.length || 'One or more'} unresolved verification finding${unresolvedIssues.length === 1 ? '' : 's'} will be recorded in the export.`
                  : 'The current layout is contained, meets its setback, and has no reported exclusion or collision overlap.'}
              </p>
            </div>

            {unresolvedIssues.length > 0 && (
              <div className="border border-warning/40 bg-warning/5 p-3 rounded-lg">
                <p className="mb-2 text-[10px] font-bold uppercase tracking-wider text-warning">
                  Unresolved checks
                </p>
                <ul className="space-y-1" aria-label="Unresolved verification checks">
                  {unresolvedIssues.map(issue => (
                    <li key={issue.id} className="flex items-start justify-between gap-3 text-text-secondary">
                      <span>{issue.label}</span>
                      <span className="shrink-0 font-mono text-warning">
                        {issue.state} · {issue.featureIds.length} {issue.featureIds.length === 1 ? 'feature' : 'features'}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            )}

            <p className="border-l-2 border-primary pl-3 text-text-secondary">
              {FORMAT_LIMITATIONS[format]}
            </p>
          </div>
        )}

        <DialogFooter>
          <button
            type="button"
            onClick={() => onOpenChange(false)}
            disabled={exporting}
            className="min-h-10 border border-border px-3 py-2 text-xs font-semibold text-text-secondary disabled:opacity-40 rounded-md"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={!bundle || !format || exporting || bundle.features.length === 0}
            className="inline-flex min-h-10 items-center justify-center gap-2 border border-primary bg-primary/10 px-4 py-2 text-xs font-bold text-primary disabled:opacity-40 rounded-md"
            data-testid="studio-export-confirm"
          >
            <Download className="h-4 w-4" aria-hidden />
            {exporting ? 'Exporting…' : `Export ${format ? FORMAT_LABELS[format] : ''}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
